<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import InboxAccessesAPI from 'dashboard/api/inboxAccesses';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();
const selectedAgentIds = ref([]);
const selectedTeamIds = ref([]);
const accessType = ref('agents');
const isAgentListUpdating = ref(false);

const agentList = computed(() => store.getters['agents/getAgents']);
const teamList = computed(() => store.getters['teams/getTeams']);

const selectedAgentNames = computed(() =>
  selectedAgentIds.value.map(
    id => agentList.value.find(a => a.id === id)?.name ?? ''
  )
);

const selectedTeamNames = computed(() =>
  selectedTeamIds.value.map(
    id => teamList.value.find(team => team.id === id)?.name ?? ''
  )
);

const agentMenuItems = computed(() =>
  agentList.value
    .filter(({ id }) => !selectedAgentIds.value.includes(id))
    .map(({ id, name, thumbnail, avatar_url }) => ({
      label: name,
      value: id,
      action: 'select',
      thumbnail: { name, src: thumbnail || avatar_url || '' },
    }))
);

const teamMenuItems = computed(() =>
  teamList.value
    .filter(({ id }) => !selectedTeamIds.value.includes(id))
    .map(({ id, name }) => ({ label: name, value: id, action: 'select' }))
);

const handleAgentAdd = ({ value }) => {
  if (!selectedAgentIds.value.includes(value)) {
    selectedAgentIds.value.push(value);
  }
};

const handleAgentRemove = index => {
  selectedAgentIds.value.splice(index, 1);
};

const handleTeamAdd = ({ value }) => {
  if (!selectedTeamIds.value.includes(value)) selectedTeamIds.value.push(value);
};

const handleTeamRemove = index => {
  selectedTeamIds.value.splice(index, 1);
};

const fetchInboxAccess = async () => {
  try {
    const { data } = await InboxAccessesAPI.get(props.inbox.id);
    accessType.value = data.access_type;
    selectedAgentIds.value = data.user_ids;
    selectedTeamIds.value = data.team_ids;
  } catch (error) {
    // Keep the collaborator editor usable if a legacy deployment has no access endpoint yet.
    const response = await store.dispatch('inboxMembers/get', {
      inboxId: props.inbox.id,
    });
    selectedAgentIds.value = response.data.payload.map(member => member.id);
  }
};

const updateAccess = async () => {
  isAgentListUpdating.value = true;
  try {
    const { data } = await InboxAccessesAPI.update({
      inboxId: props.inbox.id,
      accessType: accessType.value,
      userIds: selectedAgentIds.value,
      teamIds: selectedTeamIds.value,
    });
    selectedAgentIds.value = data.user_ids;
    selectedTeamIds.value = data.team_ids;
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
  isAgentListUpdating.value = false;
};

const setDefaults = () => {
  fetchInboxAccess();
  store.dispatch('teams/get').catch(() => {});
};

// Watch only inbox.id to avoid unnecessary refetches when other properties change
watch(() => props.inbox.id, setDefaults);

onMounted(setDefaults);
</script>

<template>
  <div>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_ACCESS.TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_ACCESS.SUB_TEXT')"
      class="[&>div]:!items-start"
    >
      <div class="mb-3 flex gap-2 rounded-xl bg-n-alpha-2 p-1">
        <button
          type="button"
          class="flex-1 rounded-lg px-3 py-2 text-sm font-medium"
          :class="
            accessType === 'agents'
              ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          @click="accessType = 'agents'"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_ACCESS.AGENTS') }}
        </button>
        <button
          type="button"
          class="flex-1 rounded-lg px-3 py-2 text-sm font-medium"
          :class="
            accessType === 'teams'
              ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11'
          "
          @click="accessType = 'teams'"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_ACCESS.TEAMS') }}
        </button>
      </div>
      <div
        class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
      >
        <TagInput
          v-if="accessType === 'agents'"
          :model-value="selectedAgentNames"
          :placeholder="$t('INBOX_MGMT.ADD.AGENTS.PICK_AGENTS')"
          :menu-items="agentMenuItems"
          show-dropdown
          skip-label-dedup
          :auto-open-dropdown="false"
          @add="handleAgentAdd"
          @remove="handleAgentRemove"
        />
        <TagInput
          v-else
          :model-value="selectedTeamNames"
          :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_ACCESS.PICK_TEAMS')"
          :menu-items="teamMenuItems"
          show-dropdown
          skip-label-dedup
          :auto-open-dropdown="false"
          @add="handleTeamAdd"
          @remove="handleTeamRemove"
        />
      </div>

      <template #extra>
        <div class="grid grid-cols-1 lg:grid-cols-8">
          <div class="col-span-1 lg:col-span-2" />
          <div class="col-span-1 lg:col-span-6 mt-4 justify-self-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isAgentListUpdating"
              @click="updateAccess"
            />
          </div>
        </div>
      </template>
    </SettingsFieldSection>
  </div>
</template>
